import React from "react";
import CheckboxNew from "@/components/Core/Checkbox/CheckboxNew";
import { FormLabel } from "../../Core/AppText/AppText";
import { FilterOption } from "./FilterGroup";

interface CheckboxFilterGroupProps {
	/** Already translated. */
	label: string;
	options: FilterOption[];
	selected: string[];
	onChange: (selected: string[]) => void;
	disabled?: boolean;
	/**
	 * Already translated label for the "no constraint" row (e.g. "Alla"),
	 * checked whenever nothing else is.
	 */
	allLabel: string;
}

/**
 * One group of a batch filter rendered as a plain checkbox list, for groups
 * short enough that the options are worth showing up front instead of hiding
 * them behind a picker (see {@link FilterGroup} for the picker variant).
 *
 * An empty selection means "no constraint", surfaced as a checked `allLabel`
 * row: checking it clears the group, and checking any option clears it again.
 * Renders nothing when there is nothing to pick from.
 */
const CheckboxFilterGroup: React.FC<CheckboxFilterGroupProps> = ({
	label,
	options,
	selected,
	onChange,
	disabled,
	allLabel,
}) => {
	if (!options.length) return null;

	const isAll = !selected.length;

	function toggle(key: string) {
		onChange(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
	}

	return (
		<div className="mb-5">
			{/* Same FormLabel as the date fields, so every label in the column
			    shares one typography source rather than a copied style */}
			<FormLabel>{label}</FormLabel>

			<div className="flex flex-col gap-2">
				<CheckboxNew
					label={allLabel}
					skipTranslation
					checked={isAll}
					disabled={disabled}
					// Already-checked means the group is unconstrained, so there is
					// nothing left to clear
					onCheckedChange={() => !isAll && onChange([])}
				/>

				{options.map((option) => (
					<CheckboxNew
						key={option.key}
						label={option.name}
						skipTranslation
						checked={selected.includes(option.key)}
						disabled={disabled}
						onCheckedChange={() => toggle(option.key)}
					/>
				))}
			</div>
		</div>
	);
};

export default CheckboxFilterGroup;
